import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import type { DropdownOption, UserData } from "@/types/backlog";
import { applyDropdownColors } from "@/lib/constants";

interface Dropdown {
  id: string;
  name: string;
}

export const useDropdownData = () => {
  return useQuery({
    queryKey: ["dropdown-data"],
    queryFn: async () => {
      // Fetch all data in parallel
      const [dropdownsRes, optionsRes, profilesRes] = await Promise.all([
        supabase.from("dropdowns").select("id, name"),
        supabase
          .from("dropdown_options")
          .select("id, dropdown_id, label, order_index, sort_index, color, kind, is_default")
          .order("order_index"),
        supabase.from("profiles").select("*").order("first_name"),
      ]);

      if (dropdownsRes.error) throw dropdownsRes.error;
      if (optionsRes.error) throw optionsRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const dropdowns = dropdownsRes.data as Dropdown[];
      const options = optionsRes.data as DropdownOption[];
      // Filter out deactivated users from assignee selection
      const profiles = (profilesRes.data as (UserData & { deactivated_at?: string | null })[]).filter(
        (p) => !p.deactivated_at,
      ) as UserData[];

      // Define the preferred order for assignees
      const preferredOrder = [
        "Mykola Kolomyets",
        "Russ Mac",
        "Russell Holland",
        "Stephen Khouri",
        "Justin Boswell",
        "Joshua Caleb",
        "Craig Tortolani",
      ];

      // Sort profiles by preferred order
      const sortedProfiles = profiles.sort((a, b) => {
        const nameA =
          a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.first_name || a.last_name || a.email;
        const nameB =
          b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.first_name || b.last_name || b.email;

        const indexA = preferredOrder.indexOf(nameA);
        const indexB = preferredOrder.indexOf(nameB);

        // If both are in preferred list, sort by preferred order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // If only A is in preferred list, A comes first
        if (indexA !== -1) return -1;
        // If only B is in preferred list, B comes first
        if (indexB !== -1) return 1;
        // Otherwise, sort alphabetically
        return nameA.localeCompare(nameB);
      });

      // Organize options by dropdown type
      const statusDropdown = dropdowns.find((d) => d.name === "Status");
      const priorityDropdown = dropdowns.find((d) => d.name === "Priority");
      const ticketTypeDropdown = dropdowns.find((d) => d.name === "Ticket Type");

      // Resolve chip colours from the stored color column (palette-hash fallback).

      return {
        // The Status dropdown's id, so the status-management page can add options to it.
        statusDropdownId: statusDropdown?.id ?? null,
        statuses: statusDropdown ? applyDropdownColors(options.filter((o) => o.dropdown_id === statusDropdown.id)) : [],
        // The Priority dropdown's id, so the priority-management page can add options to it.
        priorityDropdownId: priorityDropdown?.id ?? null,
        priorities: priorityDropdown
          ? applyDropdownColors(options.filter((o) => o.dropdown_id === priorityDropdown.id))
          : [],
        // The Ticket Type dropdown's id, so the type-management page can add options to it.
        ticketTypeDropdownId: ticketTypeDropdown?.id ?? null,
        ticketTypes: ticketTypeDropdown
          ? applyDropdownColors(options.filter((o) => o.dropdown_id === ticketTypeDropdown.id))
          : [],
        profiles: sortedProfiles,
        allOptions: applyDropdownColors(options),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — dropdown options rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
